<template>
    <div class="to-department-overview">
        <div class="card">
            <div class="card-header d-flex justify-content-between align-items-center">
                <h6 class="mb-0">Обзор отдела ТО</h6>
                <div class="d-flex align-items-center">
                    <b-form-select 
                        v-model="selectedPeriod" 
                        size="sm" 
                        style="max-width: 250px; margin-right: 10px;" 
                        :options="availableQuarters"
                        @change="onPeriodChange"
                    ></b-form-select>
                    <b-button 
                        size="sm" 
                        variant="outline-primary"
                        @click="refreshData"
                        :disabled="loading"
                        class="mr-2"
                    >
                        <i class="fas fa-sync-alt" :class="{ 'fa-spin': loading }"></i>
                    </b-button>
                    <!-- Кнопка фиксации квартала (только для administrator, president, accountant, head-accountant) -->
                    <b-button 
                        v-if="canFixQuarter"
                        size="sm" 
                        variant="warning"
                        @click="fixQuarter"
                        :disabled="loading || isQuarterFixed || fixingQuarter"
                        class="mr-2"
                    >
                        <i class="fas fa-lock" :class="{ 'fa-spin': fixingQuarter }"></i>
                        {{ isQuarterFixed ? 'Зафиксировано' : (fixingQuarter ? 'Фиксация...' : 'Зафиксировать') }}
                    </b-button>
                </div>
            </div>
            <div class="card-body">
                <!-- Лоадер -->
                <div v-if="loading" class="text-center py-4">
                    <div class="spinner-border text-primary" role="status">
                        <span class="sr-only">Загрузка...</span>
                    </div>
                    <p class="mt-2">Загрузка данных отдела...</p>
                </div>

                <!-- Общая статистика отдела -->
                <div v-else>
                <div class="department-stats mb-4">
                    <div class="row">
                        <div class="col-md-2">
                            <div class="stat-card text-center">
                                <div class="stat-value text-secondary">{{ departmentStats.totalDeclarations }}</div>
                                <div class="stat-label">Деклараций</div>
                            </div>
                        </div>
                        <div class="col-md-4">
                            <div class="stat-card text-center">
                                <div class="stat-value text-success">{{ formatCurrency(departmentStats.totalMargin) }}</div>
                                <div class="stat-label">Общая маржинальная прибыль</div>
                            </div>
                        </div>
                        <div class="col-md-3">
                            <div class="stat-card text-center">
                                <div class="stat-value text-info">{{ formatCurrency(departmentStats.totalNewClientsBonuses) }}</div>
                                <div class="stat-label">Бонусы за новых клиентов</div>
                            </div>
                        </div>
                        <div class="col-md-3">
                            <div class="stat-card text-center">
                                <div class="stat-value text-primary">{{ formatCurrency(departmentStats.totalBonuses) }}</div>
                                <div class="stat-label">Общие бонусы</div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Таблица сотрудников -->
                <div class="employees-table">
                    <div class="table-responsive">
                        <table class="table table-hover">
                            <thead class="thead-light">
                                <tr>
                                    <th style="height: 36px;">Сотрудник</th>
                                    <th style="height: 36px;">Декларации</th>
                                    <th style="height: 36px;">Маржинальность</th>
                                    <th style="height: 36px;">Сверхдоход</th>
                                    <th style="height: 36px;">Бонус за новых клиентов</th>
                                    <th style="height: 36px;">Общий бонус</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="employee in employees" :key="employee.id" @click="openUserSidebar(employee)" class="employee-row">
                                    <td>
                                        <div class="employee-info">
                                            <div class="employee-name">{{ employee.name }}</div>
                                        </div>
                                    </td>
                                    <td>
                                        <div class="declarations-info">
                                            <div class="declarations-count">
                                                <span class="text-primary font-weight-bold">{{ employee.declarations }}</span>
                                                <small class="text-muted d-block">из 90 плана</small>
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <span class="text-success font-weight-bold">
                                            {{ formatCurrency(employee.margin) }}
                                        </span>
                                    </td>
                                    <td>
                                        <span class="text-secondary font-weight-bold">
                                            {{ formatCurrency(employee.superIncome) }}
                                        </span>
                                    </td>
                                    <td>
                                        <span class="text-success font-weight-bold">
                                            {{ formatCurrency(employee.newClientsBonus) }}
                                        </span>
                                    </td>
                                    <td>
                                        <span class="text-primary font-weight-bold">
                                            {{ formatCurrency(employee.bonus) }}
                                        </span>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <!-- Графики с табами -->
                <div class="charts-section mt-4">
                    <div class="card bdr-tabs">
                        <div class="card-header">
                            <ul class="nav nav-tabs card-header-tabs" role="tablist">
                                <li class="nav-item">
                                    <a class="nav-link" 
                                       :class="{ active: activeChartTab === 'declarations' }"
                                       @click="switchChartTab('declarations')"
                                       href="#"
                                       role="tab">
                                        <i class="fas fa-file-alt mr-2"></i>
                                        Декларации
                                    </a>
                                </li>
                                <li class="nav-item">
                                    <a class="nav-link" 
                                       :class="{ active: activeChartTab === 'marginality' }"
                                       @click="switchChartTab('marginality')"
                                       href="#"
                                       role="tab">
                                        <i class="fas fa-ruble-sign mr-2"></i>
                                        Маржинальность
                                    </a>
                                </li>
                            </ul>
                        </div>
                        <div class="card-body">
                            <!-- График деклараций -->
                            <div v-if="activeChartTab === 'declarations'" class="chart-container">
                                <MotivationDeclarationsChart 
                                    :key="`declarations-${chartKey}`"
                                    :rawChartData="declarationsChartData"
                                    :loading="declarationsChartLoading"
                                    title="Количество деклараций"
                                />
                            </div>

                            <!-- График маржинальности -->
                            <div v-if="activeChartTab === 'marginality'" class="chart-container">
                                <MotivationMarginalityChart 
                                    :key="`marginality-${chartKey}`"
                                    :rawChartData="marginalityChartData"
                                    :loading="marginalityChartLoading"
                                    title="Общая маржинальность (₽)"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Информация о кварталах -->
                <div class="mt-4">
                    <small class="text-muted">
                        <strong>Кварталы считаются по следующим месяцам:</strong><br>
                        Q1: декабрь, январь, февраль | 
                        Q2: март, апрель, май | 
                        Q3: июнь, июль, август | 
                        Q4: сентябрь, октябрь, ноябрь
                    </small>
                </div>

                </div>
            </div>
        </div>

        <!-- Сайдбар с персональной статистикой пользователя -->
        <div v-if="sidebarOpen" class="user-sidebar-overlay" @click="closeUserSidebar">
            <div class="user-sidebar" @click.stop>
                <div class="user-sidebar-header">
                    <h5 class="mb-0">Персональная статистика</h5>
                    <button type="button" class="btn-close" @click="closeUserSidebar">
                        <i class="fas fa-times"></i>
                    </button>
                </div>
                <div class="user-sidebar-body">
                    <TOPersonalStats
                        v-if="selectedUser"
                        :user-id="selectedUser.id"
                        :period="selectedPeriod"
                        :hide-motivation-text="true"
                        :open-in-new-window="true"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { 
    API_MOTIVATION2025_TO,
    // ТО API
    API_MOTIVATION2025_CHARTS_DECLARATIONS,
    API_MOTIVATION2025_CHARTS_TO_MARGINALITY,
    API_MOTIVATION2025_FIX_QUARTER
} from '@/constants/api'

import MotivationDeclarationsChart from '@components/charts/MotivationDeclarationsChart.vue'
import MotivationMarginalityChart from '@components/charts/MotivationMarginalityChart.vue'
import TOPersonalStats from './TOPersonalStats.vue'

export default {
    name: 'TODepartmentOverview',
    components: {
        MotivationDeclarationsChart,
        MotivationMarginalityChart,
        TOPersonalStats
    },
    props: {
        period: {
            type: String,
            default: 'current'
        },
        motivationData: {
            type: Array,
            default: function() {
                return []
            }
        }
    },
    data() {
        return {
            loading: false,
            dataLoaded: false, // Флаг для предотвращения двойной загрузки
            selectedPeriod: this.period || this.getCurrentQuarter(),
            periodOptions: [],
            activeChartTab: 'declarations',
            declarationsChartLoading: false,
            marginalityChartLoading: false,
            declarationsChartData: [],
            marginalityChartData: [],
            chartKey: 0, // Ключ для принудительного перерендера графиков
            departmentStats: {
                totalEmployees: 0,
                totalMargin: 0,
                totalDeclarations: 0,
                totalBonuses: 0,
                totalNewClientsBonuses: 0
            },
            employees: [],
            isQuarterFixed: false,
            fixingQuarter: false,
            // Состояние для сайдбара пользователя
            sidebarOpen: false,
            selectedUser: null
        }
    },
    watch: {
        motivationData: {
            handler(newData) {
                if (newData.length > 0) {
                    this.processMotivationData(newData)
                    // Останавливаем загрузку через API если получили данные через props
                    this.loading = false
                    this.dataLoaded = true // Помечаем что данные загружены
                    
                    // Обновляем ключ для перерендера графиков при получении props данных
                    this.$nextTick(() => {
                        this.chartKey++
                    })
                }
            },
            immediate: true
        }
    },
    computed: {
        canFixQuarter() {
            // Проверяем, может ли пользователь фиксировать кварталы
            const user = this.$store.getters['auth/currentUser']
            return user && user.role && (
                user.role.includes('administrator') || 
                user.role.includes('president') ||
                user.role.includes('accountant') ||
                user.role.includes('head-accountant')
            )
        },
        availableQuarters() {
            const now = new Date()
            const year = now.getFullYear()
            const month = now.getMonth() + 1
            
            const quarters = []
            const minYear = 2025
            const minQuarter = 4 // Q4 2025 - минимальный квартал для выбора
            
            // Перебираем годы начиная с 2025
            for (let y = minYear; y <= year; y++) {
                if (y === minYear && y === year) {
                    // Для 2025 года, когда это текущий год
                    // Q4 доступен начиная с сентября
                    if (month >= 9) {
                        quarters.push({ value: `${y}-Q4`, text: `Q4 ${y} (сентябрь, октябрь, ноябрь)` })
                    }
                    // Если мы в декабре 2025, добавляем Q1 2026
                    if (month === 12) {
                        quarters.push({ value: `${y + 1}-Q1`, text: `Q1 ${y + 1} (декабрь, январь, февраль)` })
                    }
                } else if (y === minYear) {
                    // Для 2025 года, когда это прошлый год - все кварталы
                    quarters.push({ value: `${y}-Q1`, text: `Q1 ${y} (декабрь, январь, февраль)` })
                    quarters.push({ value: `${y}-Q2`, text: `Q2 ${y} (март, апрель, май)` })
                    quarters.push({ value: `${y}-Q3`, text: `Q3 ${y} (июнь, июль, август)` })
                    quarters.push({ value: `${y}-Q4`, text: `Q4 ${y} (сентябрь, октябрь, ноябрь)` })
                } else if (y === year) {
                    // Для текущего года (после 2025) - все актуальные кварталы
                    // Q1: декабрь предыдущего года, январь и февраль текущего
                    if (month === 12 || month === 1 || month === 2) {
                        const q1Year = month === 12 ? y + 1 : y
                        quarters.push({ value: `${q1Year}-Q1`, text: `Q1 ${q1Year} (декабрь, январь, февраль)` })
                    } else if (month >= 3) {
                        quarters.push({ value: `${y}-Q1`, text: `Q1 ${y} (декабрь, январь, февраль)` })
                    }
                    if (month >= 3) quarters.push({ value: `${y}-Q2`, text: `Q2 ${y} (март, апрель, май)` })
                    if (month >= 6) quarters.push({ value: `${y}-Q3`, text: `Q3 ${y} (июнь, июль, август)` })
                    if (month >= 9) quarters.push({ value: `${y}-Q4`, text: `Q4 ${y} (сентябрь, октябрь, ноябрь)` })
                } else {
                    // Для прошлых лет (после 2025) - все кварталы
                    quarters.push({ value: `${y}-Q1`, text: `Q1 ${y} (декабрь, январь, февраль)` })
                    quarters.push({ value: `${y}-Q2`, text: `Q2 ${y} (март, апрель, май)` })
                    quarters.push({ value: `${y}-Q3`, text: `Q3 ${y} (июнь, июль, август)` })
                    quarters.push({ value: `${y}-Q4`, text: `Q4 ${y} (сентябрь, октябрь, ноябрь)` })
                }
            }
            
            // Разворачиваем массив, чтобы текущий квартал был сверху
            return quarters.reverse()
        }
    },
    mounted() {
        // Инициализируем квартал если не задан
        if (!this.selectedPeriod || this.selectedPeriod === 'current') {
            this.selectedPeriod = this.getCurrentQuarter()
        }
        
        // Всегда загружаем данные - если есть props данные, они будут обработаны в watch
        this.loadData()
        
        // Загружаем данные графиков при инициализации
        this.$nextTick(( ) => {
            this.loadChartData()
        })
    },
    methods: {
        onPeriodChange() {
            this.dataLoaded = false // Сбрасываем флаг при смене периода
            this.loadData()
            // Перезагружаем данные графиков при смене периода
            this.loadChartData()
        },
        refreshData() {
            // Сбрасываем флаг загрузки данных, чтобы перезагрузить их
            this.dataLoaded = false
            // Перезагружаем данные мотивации и графиков
            this.loadData()
        },
        loadData() {
            // Предотвращаем двойную загрузку
            if (this.dataLoaded) {
                return
            }
            
            this.dataLoaded = true
            // Загружаем данные мотивации с сервера
            this.loadMotivationData()
            // Загружаем данные для графиков
            this.loadChartData()
        },
        loadMotivationData() {
            this.loading = true
            
            const params = {
                quarter: this.selectedPeriod,
                department_id: 2 // Отдел ТО
            }
            
            axios.get(API_MOTIVATION2025_TO, { params })
                .then(response => {
                    if (response.data.success) {
                        this.processMotivationData(response.data.data)
                        // Проверяем, зафиксирован ли квартал
                        this.isQuarterFixed = response.data.is_fixed || false
                    } else {
                        console.error('API Error:', response.data.message)
                    }
                })
                .catch(error => {
                    console.error('Error loading motivation data:', error)
                })
                .finally(() => {
                    this.loading = false
                })
        },
        processMotivationData(data) {
            // Обрабатываем данные мотивации для отображения
            this.employees = data.map(user => ({
                id: user.user_id,
                name: user.user_name,
                declarations: user.declarations_count,
                margin: user.margin.total_margin,
                superIncome: user.super_income,
                newClientsBonus: user.new_clients?.bonus || 0,
                bonus: user.bonus
            }))
            
            // Обновляем статистику отдела
            this.updateDepartmentStats()
        },
        updateDepartmentStats() {
            if (this.employees.length > 0) {
                this.departmentStats.totalEmployees = this.employees.length
                this.departmentStats.totalMargin = this.employees.reduce((sum, emp) => sum + emp.margin, 0)
                this.departmentStats.totalDeclarations = this.employees.reduce((sum, emp) => sum + emp.declarations, 0)
                this.departmentStats.totalBonuses = this.employees.reduce((sum, emp) => sum + emp.bonus, 0)
                this.departmentStats.totalNewClientsBonuses = this.employees.reduce((sum, emp) => sum + emp.newClientsBonus, 0)
            }
        },
        getCurrentQuarter() {
            const now = new Date()
            const year = now.getFullYear()
            const month = now.getMonth() + 1
            const minYear = 2025
            const minQuarter = 4 // Q4 2025 - минимальный квартал
            
            let currentQuarter = ''
            
            // Финансовая схема кварталов (год начинается с декабря):
            // Q1: декабрь, январь, февраль (12,1,2)
            // Q2: март, апрель, май (3,4,5)
            // Q3: июнь, июль, август (6,7,8)
            // Q4: сентябрь, октябрь, ноябрь (9,10,11)
            if (month === 12 || month === 1 || month === 2) {
                currentQuarter = month === 12 ? `${year + 1}-Q1` : `${year}-Q1`
            } else if (month >= 3 && month <= 5) {
                currentQuarter = `${year}-Q2`
            } else if (month >= 6 && month <= 8) {
                currentQuarter = `${year}-Q3`
            } else if (month >= 9 && month <= 11) {
                currentQuarter = `${year}-Q4`
            }
            
            // Проверяем, что квартал не раньше Q4 2025
            const [qYear, qNum] = currentQuarter.split('-Q')
            const quarterNum = parseInt(qNum)
            
            if (qYear < minYear || (qYear == minYear && quarterNum < minQuarter)) {
                return '2025-Q4' // Возвращаем минимальный доступный квартал
            }
            
            return currentQuarter || '2025-Q4'
        },
        formatCurrency(amount) {
            return new Intl.NumberFormat('ru-RU', {
                style: 'currency',
                currency: 'RUB',
                minimumFractionDigits: 0
            }).format(amount)
        },
        fixQuarter() {
            if (!this.canFixQuarter) {
                this.$toast.error('У вас нет прав для фиксации кварталов')
                return
            }

            if (this.isQuarterFixed) {
                this.$toast.info('Квартал уже зафиксирован')
                return
            }

            this.fixingQuarter = true

            const data = {
                quarter: this.selectedPeriod,
                department_id: 2
            }

            axios.post(API_MOTIVATION2025_FIX_QUARTER, data)
                .then(response => {
                    if (response.data.success) {
                        this.isQuarterFixed = true
                        this.$toast.success(response.data.message)
                    } else {
                        this.$toast.error(response.data.message || 'Ошибка при фиксации квартала')
                    }
                })
                .catch(error => {
                    console.error('Error fixing quarter:', error)
                    this.$toast.error('Ошибка соединения с сервером')
                })
                .finally(() => {
                    this.fixingQuarter = false
                })
        },
        switchChartTab(tab) {
            this.activeChartTab = tab
        },
        loadChartData() {
            this.loadDeclarationsChartData()
            this.loadMarginalityChartData()
        },
        loadDeclarationsChartData() {
            this.declarationsChartLoading = true
            
            const params = {
                quarter: this.selectedPeriod,
                department_id: 2
            }
            
            axios.get(API_MOTIVATION2025_CHARTS_DECLARATIONS, { params })
                .then(response => {
                    if (response.data.success) {
                        this.declarationsChartData = response.data.data
                        
                        // Обновляем ключ после успешной загрузки данных
                        this.$nextTick(() => {
                            this.chartKey++
                        })
                    } else {
                        console.error('API Error:', response.data.message)
                    }
                })
                .catch(error => {
                    console.error('Error loading declarations chart data:', error)
                })
                .finally(() => {
                    this.declarationsChartLoading = false
                })
        },
        loadMarginalityChartData() {
            this.marginalityChartLoading = true
            
            const params = {
                quarter: this.selectedPeriod,
                department_id: 2
            }
            
            axios.get(API_MOTIVATION2025_CHARTS_TO_MARGINALITY, { params })
                .then(response => {
                    if (response.data.success) {
                        this.marginalityChartData = response.data.data
                        
                        // Обновляем ключ после успешной загрузки данных
                        this.$nextTick(() => {
                            this.chartKey++
                        })
                    } else {
                        console.error('API Error:', response.data.message)
                    }
                })
                .catch(error => {
                    console.error('Error loading marginality chart data:', error)
                })
                .finally(() => {
                    this.marginalityChartLoading = false
                })
        },
        openUserSidebar(employee) {
            this.selectedUser = employee
            this.sidebarOpen = true
        },
        closeUserSidebar() {
            this.sidebarOpen = false
            this.selectedUser = null
        }
    }
}
</script>

<style scoped>
.stat-card {
    padding: 1rem;
    border: 1px solid #dee2e6;
    border-radius: 0.5rem;
    background-color: #f8f9fa;
}

.stat-value {
    font-size: 1rem;
    font-weight: bold;
    margin-bottom: 0.25rem;
}

.stat-label {
    font-size: 0.7rem;
    color: #6c757d;
}

.employee-info {
    display: flex;
    flex-direction: column;
}

.employee-name {
    font-weight: 500;
}

.declarations-info {
    min-width: 100px;
}

.declarations-count {
    text-align: center;
}

.table th {
    border-top: none;
    font-weight: 600;
    color: #495057;
}

.table td {
    vertical-align: middle;
}

.badge {
    font-size: 0.75rem;
}

/* Chart styles */
.chart-container {
    position: relative;
    height: 400px;
}

.chart-wrapper {
    position: relative;
    height: 100%;
}

.nav-tabs .nav-link {
    border: none;
    border-bottom: 2px solid transparent;
    color: #6c757d;
    font-weight: 500;
}

.nav-tabs .nav-link:hover {
    border-color: transparent;
    border-bottom-color: #dee2e6;
    color: #495057;
}

.nav-tabs .nav-link.active {
    color: #0D6247;
    border-bottom-color: #0D6247;
    background-color: transparent;
}

.nav-tabs .nav-link i {
    font-size: 0.875rem;
}

/* Стили для кликабельных строк сотрудников */
.employee-row {
    cursor: pointer;
    transition: background-color 0.2s ease;
}

.employee-row:hover {
    background-color: #f8f9fa;
}

.employee-row .employee-name {
    font-weight: 600;
}

.employee-row:hover .employee-name {
    color: #0D6247;
    font-weight: 600;
}

/* Стили для сайдбара пользователя */
.user-sidebar-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 1050;
    display: flex;
    justify-content: flex-end;
    align-items: stretch;
}

.user-sidebar {
    width: 80%;
    max-width: 1200px;
    background-color: white;
    box-shadow: -2px 0 10px rgba(0, 0, 0, 0.1);
    display: flex;
    flex-direction: column;
    height: 100vh;
    overflow: hidden;
}

.user-sidebar-header {
    padding: 1rem 1.5rem;
    border-bottom: 1px solid #dee2e6;
    background-color: #f8f9fa;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-shrink: 0;
}

.user-sidebar-header h5 {
    color: #0D6247;
    font-weight: 600;
}

.btn-close {
    background: none;
    border: none;
    font-size: 1.25rem;
    color: #6c757d;
    cursor: pointer;
    padding: 0.25rem;
    border-radius: 0.25rem;
    transition: all 0.2s ease;
}

.btn-close:hover {
    color: #495057;
    background-color: #e9ecef;
}

.user-sidebar-body {
    flex: 1;
    overflow-y: auto;
    padding: 0;
}

/* Стили для скроллбара */
.user-sidebar-body::-webkit-scrollbar {
    width: 6px;
}

.user-sidebar-body::-webkit-scrollbar-track {
    border-radius: 3px;
}

.user-sidebar-body::-webkit-scrollbar-thumb {
    border-radius: 3px;
}

/* Адаптивность для мобильных устройств */
@media (max-width: 768px) {
    .user-sidebar {
        width: 100%;
        max-width: none;
    }
}
</style>
